// Pure helpers and config for the /messages list, extracted from MessagesPage.
// The classification/dialog functions are side-effect free and unit-tested in
// messageListHelpers.test.js.
import { format, isToday, isThisYear } from "date-fns";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

export const formatHumanDateTime = (dateValue) => {
  if (!dateValue) return "";
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return "";
  return format(dateValue, "MMMM d, yyyy 'at' h:mm a");
};

// Compact timestamp for message cards: time today, month/day this year,
// otherwise month/day/year.
export const formatCompactTime = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) return "";
  if (isToday(dateValue)) return format(dateValue, "h:mm a");
  if (isThisYear(dateValue)) return format(dateValue, "MMM d");
  return format(dateValue, "MMM d, yyyy");
};

export const SWIPE_ACTION_META = {
  archive: {
    label: "Archive",
    color: "warning.main",
    Icon: ArchiveOutlinedIcon,
    justify: "flex-start",
    flexDirection: "row",
  },
  unarchive: {
    label: "Unarchive",
    color: "success.main",
    Icon: UnarchiveOutlinedIcon,
    justify: "flex-start",
    flexDirection: "row",
  },
  delete: {
    label: "Delete",
    color: "error.main",
    Icon: DeleteOutlineIcon,
    justify: "flex-end",
    flexDirection: "row-reverse",
  },
  discard: {
    label: "Discard",
    color: "error.main",
    Icon: DeleteOutlineIcon,
    justify: "flex-end",
    flexDirection: "row-reverse",
  },
  restore: {
    label: "Restore",
    color: "success.main",
    Icon: UnarchiveOutlinedIcon,
    justify: "flex-start",
    flexDirection: "row",
  },
};

export const PRIMARY_TABS = [
  { value: 0, label: "Received" },
  { value: 1, label: "Buried" },
  { value: 2, label: "Memories" },
];

export const READ_FILTERS = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
];

export const SORT_OPTIONS = [
  { value: "recent", label: "Most recent", shortLabel: "Recent" },
  { value: "sender", label: "Sender (A–Z)", shortLabel: "Sender" },
  { value: "place", label: "Place / title (A–Z)", shortLabel: "Place" },
];

export function getSortShortLabel(sortBy) {
  const match = SORT_OPTIONS.find((option) => option.value === sortBy);
  return match ? match.shortLabel : "Recent";
}

function messageTimeMillis(message) {
  const ts = message?.sentAt || message?.createdAt;
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  const time = new Date(ts).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function senderSortKey(message) {
  return String(message?.senderDisplayName || message?.senderPublicHandle || "").toLowerCase();
}

function placeSortKey(message) {
  return String(
    message?.subject || message?.address || message?.location?.address || ""
  ).toLowerCase();
}

// Returns a new array sorted by the chosen mode. Ties fall back to most-recent.
export function sortMessages(list, sortBy) {
  const items = Array.isArray(list) ? list.slice() : [];
  if (sortBy === "sender") {
    return items.sort(
      (a, b) =>
        senderSortKey(a).localeCompare(senderSortKey(b)) ||
        messageTimeMillis(b) - messageTimeMillis(a)
    );
  }
  if (sortBy === "place") {
    return items.sort(
      (a, b) =>
        placeSortKey(a).localeCompare(placeSortKey(b)) ||
        messageTimeMillis(b) - messageTimeMillis(a)
    );
  }
  return items.sort((a, b) => messageTimeMillis(b) - messageTimeMillis(a));
}

export function isDraftCapsuleMessage(message) {
  return (
    message?.lifecycleStatus === "draft" ||
    message?.status === "draft" ||
    message?.isDraft === true
  );
}

export function isSponsoredCapsuleMessage(message) {
  return String(message?.capsuleType || "").toLowerCase() === "sponsored";
}

// Single source of truth for the swipe-action confirm dialog copy/styling.
export function getSwipeDialogConfig(action) {
  const type = action?.type;
  if (type === "archive") {
    return {
      title: "Archive message?",
      body: "This will move the message to Archived.",
      confirmColor: "warning",
      confirmLabel: "Archive",
    };
  }
  if (type === "unarchive") {
    return {
      title: "Unarchive message?",
      body: "This will move the message back into your active views.",
      confirmColor: "success",
      confirmLabel: "Unarchive",
    };
  }
  if (type === "restore") {
    return {
      title: "Restore message?",
      body: "This will restore the message to its previous view.",
      confirmColor: "success",
      confirmLabel: "Restore",
    };
  }
  if (action?.msg && isDraftCapsuleMessage(action.msg)) {
    return {
      title: "Discard draft?",
      body: "This will permanently discard this draft.",
      confirmColor: "error",
      confirmLabel: "Discard",
    };
  }
  return {
    title: "Delete message?",
    body: "This will remove the message for you, but keep it for other recipients.",
    confirmColor: "error",
    confirmLabel: "Delete",
  };
}
